// TanStack Query provider with React 19 optimizations
import React, { Suspense } from "react";
import {
  QueryClient,
  QueryClientProvider,
  MutationCache,
  QueryCache,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { isAxiosError, isCancel } from "axios";
import { showGlobalToast } from "../components/ui/toastBridge";
import { getApiErrorMessage } from "../utils/apiError";
import { queryKeys } from "../api/queryKeys";

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /**
       * Whether this mutation shows its own error UI. Defaults to whether it
       * defines `onError`. Set `true` when the caller shows the error (call-site
       * onError, try/catch, rendering `mutation.error`); set `false` when
       * `onError` only rolls back or logs.
       */
      handlesError?: boolean;
    };
  }
}

// Already surfaced by HttpClient's session-expiry flow (redirect + message).
const SESSION_ERROR_CODES = new Set(["ACCOUNT_BANNED", "DEVICE_CONFLICT"]);

/** Toast any mutation failure that nothing else shows to the user. */
function notifyMutationError(
  error: unknown,
  mutation: { meta?: { handlesError?: boolean }; options: { onError?: unknown } },
) {
  const handlesError = mutation.meta?.handlesError ?? !!mutation.options.onError;
  if (handlesError || isCancel(error)) return;

  if (isAxiosError(error)) {
    const status = error.response?.status;
    const code = error.response?.data?.error?.code ?? error.response?.data?.code;
    if (status === 401 || SESSION_ERROR_CODES.has(code)) return;
  }

  showGlobalToast(
    "error",
    getApiErrorMessage(error, "Something went wrong. Please try again."),
  );
}

// Error boundary for query errors - SOLID principle
class QueryErrorBoundary extends React.Component<
  {
    children: React.ReactNode;
    fallback?: React.ComponentType<{ error: Error; reset: () => void }>;
  },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Query Error Boundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      return (
        <FallbackComponent
          error={this.state.error}
          reset={() => this.setState({ hasError: false, error: null })}
        />
      );
    }

    return this.props.children;
  }
}

// Default error fallback component
const DefaultErrorFallback: React.FC<{ error: Error; reset: () => void }> = ({
  error,
  reset,
}) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
      <div className="text-red-600 mb-4">
        <svg
          className="w-16 h-16 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Something went wrong
      </h2>
      <p className="text-gray-600 mb-4">{error.message}</p>
      <button
        onClick={reset}
        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
      >
        Try again
      </button>
    </div>
  </div>
);

// Loading fallback component
const QueryLoadingFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
      <p className="text-gray-600">Loading application...</p>
    </div>
  </div>
);

// Create query client with optimized settings
const createQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // React 19 optimizations
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
        retry: (failureCount, error: any) => {
          // Don't retry on 4xx errors except 408, 429
          if (error?.response?.status >= 400 && error?.response?.status < 500) {
            if (
              error.response.status === 408 ||
              error.response.status === 429
            ) {
              return failureCount < 2;
            }
            return false;
          }
          // Retry up to 3 times for other errors
          return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchOnWindowFocus: false,
        refetchOnReconnect: "always",
      },
      mutations: {
        retry: (failureCount, error: any) => {
          // Don't retry mutations on client errors
          if (error?.response?.status >= 400 && error?.response?.status < 500) {
            return false;
          }
          return failureCount < 2;
        },
        // React 19 optimizations
        networkMode: "online",
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        console.error("Query error:", error, "Query key:", query.queryKey);

        // Global error handling
        if (error instanceof Error) {
          // Handle specific error types
          if (error.message.includes("Network Error")) {
            // Handle network errors
            console.warn("Network error detected, user might be offline");
          } else if (error.message.includes("401")) {
            // Handle authentication errors
            console.warn("Authentication error, redirecting to login");
            // Could dispatch a global auth action here
          }
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, variables, _context, mutation) => {
        console.error("Mutation error:", error, "Variables:", variables);
        notifyMutationError(error, mutation);
      },
      onSuccess: (data, variables, context, mutation) => {
        // Global mutation success handling
        console.log("Mutation succeeded:", mutation.options.mutationKey);
      },
    }),
  });
};

// Singleton query client instance
let queryClient: QueryClient | undefined;

export const getQueryClient = () => {
  if (!queryClient) {
    queryClient = createQueryClient();
  }
  return queryClient;
};

// Props interface
interface QueryProviderProps {
  children: React.ReactNode;
  client?: QueryClient;
}

// Main QueryProvider component with React 19 features
export const QueryProvider: React.FC<QueryProviderProps> = ({
  children,
  client,
}) => {
  const queryClientInstance = client || getQueryClient();

  return (
    <QueryClientProvider client={queryClientInstance}>
      <QueryErrorBoundary>
        <Suspense fallback={<QueryLoadingFallback />}>{children}</Suspense>
      </QueryErrorBoundary>

      {/* Development tools */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

// Hook to get query client instance
export const useQueryClient = () => {
  const client = getQueryClient();
  return client;
};

// Prefetch utilities for React 19 concurrent features
export const prefetchQueries = {
  modules: (courseCode: string) => {
    const client = getQueryClient();
    return client.prefetchQuery({
      queryKey: queryKeys.modules("list", courseCode),
      queryFn: () =>
        import("../api").then((api) =>
          api
            .getApiClient()
            .getHttpClient()
            .get(`/courses/${courseCode}/modules`),
        ),
      staleTime: 5 * 60 * 1000,
    });
  },

  moduleContents: (courseCode: string, moduleId: string | number) => {
    const client = getQueryClient();
    return client.prefetchQuery({
      queryKey: queryKeys.modules("detail", courseCode, moduleId, "contents"),
      queryFn: () =>
        import("../api").then((api) =>
          api
            .getApiClient()
            .getHttpClient()
            .get(`/courses/${courseCode}/modules/${moduleId}/contents`),
        ),
      staleTime: 2 * 60 * 1000,
    });
  },
};

export default QueryProvider;
